import React from 'react'
import { useDropzone } from 'react-dropzone'
import { FileItem } from '../../model'
import ActionButton, { ActionOption } from '../ActionButton/ActionButton'
import DefaultLoader from '../Loaders/DefaultLoader'
import DynamicFaIcon from '../DynamicFaIcon/DynamicFaIcon'
import FileIcon from './FileIcon'

interface UploadProgress {
    current: number
    total: number
}

interface UploadContainerProps {
    files: FileItem[]
    isLoading: boolean
    isUploading: boolean
    progress: UploadProgress
    message: string | null
    onUpload(files: File[]): void | Promise<void>
    onDownload(file: FileItem): void | Promise<void>
    onDelete(file: FileItem): void | Promise<void>
}

const UploadContainer: React.FC<UploadContainerProps> = ({
    files,
    isLoading,
    isUploading,
    progress,
    message,
    onUpload,
    onDownload,
    onDelete
}) => {
    const { getRootProps, getInputProps, isFocused, isDragAccept } = useDropzone({
        onDrop: onUpload,
        disabled: isUploading
    })

    const fileActions = (file: FileItem): ActionOption[] => [
        {
            text: 'Downloaden',
            type: 'default',
            action: () => onDownload(file),
            contextType: 'bestand',
            showConfirmModal: false
        },
        ...(file.canDelete
            ? [
                  {
                      text: 'Verwijderen',
                      type: 'danger',
                      action: () => onDelete(file),
                      contextType: 'bestand'
                  }
              ]
            : [])
    ]

    return (
        <>
            {message && (
                <div className='mx-6 mb-4 bg-[--tb-blushPrimary] border border-dashed rounded-lg p-4 text-center text-red-500'>
                    {message}
                </div>
            )}
            {isUploading ? (
                <div className='mx-6 bg-[--tb-blushPrimary] border border-dashed rounded-lg p-4 text-center text-[--tb-primary]'>
                    Bestand {progress.current} van {progress.total} wordt geüpload…
                </div>
            ) : (
                <div
                    {...getRootProps({
                        className:
                            'mx-6 bg-[--tb-blushPrimary] border border-dashed rounded-lg text-center cursor-pointer transition-colors border-gray-300 hover:border-[--tb-primary]',
                        style: { borderColor: isDragAccept || isFocused ? 'var(--tb-primary)' : '' }
                    })}
                >
                    <input {...getInputProps()} />
                    <div className='flex flex-row space-x-2 items-center justify-center text-[--tb-primary] p-4'>
                        <DynamicFaIcon iconName='paperclip-vertical' />
                        <p className='text-sm font-medium'>Sleep bestanden hierheen, of klik om te selecteren</p>
                    </div>
                </div>
            )}
            {isLoading ? (
                <div className='flex justify-center py-8'>
                    <DefaultLoader />
                </div>
            ) : files.length ? (
                <ul className='mt-4'>
                    {files.map((file) => (
                        <li
                            className='group relative w-full border-t last:border-b transition-colors hover:bg-[--tb-blushPrimary]'
                            key={file.id}
                        >
                            <button
                                type='button'
                                className='absolute inset-0 z-0 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[--tb-primary]'
                                onClick={() => onDownload(file)}
                                aria-label={`Download ${file.originalFilename}`}
                            />
                            <div className='pointer-events-none relative z-10 grid w-full grid-cols-3 items-center px-4 py-4 sm:grid-cols-6 sm:px-6'>
                                <div className='col-span-2 grid min-w-0 grid-cols-1 items-center gap-x-2 sm:col-span-5 sm:grid-cols-5'>
                                    <div className='flex items-center px-2 gap-x-1 col-span-3 min-w-0'>
                                        <FileIcon fileName={file.originalFilename} />
                                        <p className='truncate text-sm font-medium transition-colors group-hover:text-[--tb-primary]'>
                                            {file.originalFilename}
                                        </p>
                                    </div>
                                    <div className='col-span-2 pl-8 pr-2 text-sm text-gray-500 transition-colors group-hover:text-[--tb-primary] sm:px-2'>
                                        Geüpload: {new Date(file.created).toLocaleDateString('nl-NL')}
                                    </div>
                                </div>
                                <div className='pointer-events-auto col-span-1 flex flex-shrink-0 justify-end'>
                                    <ActionButton options={fileActions(file)} />
                                </div>
                            </div>
                        </li>
                    ))}
                </ul>
            ) : (
                <div className='text-center py-8 text-gray-500'>Nog geen bestanden geüpload</div>
            )}
        </>
    )
}

export default UploadContainer
